import { createRouter, createWebHistory } from "vue-router";
import { getSeasonTitle } from "../utils/season";

export default createRouter({
  history: createWebHistory(),
  scrollBehavior(to, _from, savedPosition) {
    // 浏览器前进/后退：恢复原滚动位置
    if (savedPosition) return savedPosition;

    // 带 hash 的锚点跳转
    if (to.hash) {
      return {
        el: to.hash,
        top: 0,
        behavior: "smooth",
      };
    }

    // 普通页面切换：回到顶部
    return { left: 0, top: 0 };
  },
  routes: [
    {
      path: "/",
      component: () => import("../layouts/PublicLayout.vue"),
      children: [
        {
          path: "",
          name: "home",
          component: () => import("../pages/HomePage.vue"),
        },

        // 走进达古
        {
          path: "about",
          name: "about",
          component: () => import("../layouts/EmptyLayout.vue"),
          meta: { title: "走进达古" },
          children: [
            {
              path: "",
              name: "about-home",
              component: () => import("../pages/EnterDagu.vue"),
              meta: { title: "走进达古" },
            },
            {
              path: "intro",
              name: "about-intro",
              component: () =>
                import("../pages/ScenicSpotIntroductionPage.vue"),
              meta: { title: "景区简介" },
            },
            {
              path: "intro/views",
              name: "about-intro-views",
              component: () =>
                import("../pages/ScenicSpotIntroductionViewsPage.vue"),
              meta: { title: "" },
              beforeEnter: (to, _from, next) => {
                const title = (to.query.title as string) || "";
                if (title) {
                  to.meta.title = title;
                }
                next();
              },
            },
            {
              path: "honor",
              name: "about-honor",
              component: () => import("../pages/ScenicSpotHonorsPage.vue"),
              meta: { title: "景区荣誉" },
            },
            {
              path: "geology",
              name: "about-geology",
              component: () => import("../pages/GeologicalSciencePage.vue"),
              meta: { title: "地质地貌" },
            },
            {
              path: "glacier-research",
              name: "about-glacier-research",
              component: () => import("../pages/GlacierResearchPage.vue"),
              meta: { title: "民俗文化", navKey: "/about/glacier-research" },
            },
            {
              path: "RedCulture",
              alias: ["redculture"],
              name: "about-redculture",
              component: () => import("../pages/GlacierResearchPage.vue"),
              meta: { title: "红军文化", navKey: "/about/RedCulture" },
            },
            {
              path: "ecology",
              name: "about-ecology",
              component: () => import("../pages/EcologicalProtectionPage.vue"),
              meta: { title: "自然生态" },
            },
            {
              path: "experiment",
              name: "about-experiment",
              component: () => import("../pages/GlacierExperimentPage.vue"),
              meta: { title: "冰川保护" },
            },
          ],
        },

        // 全景达古 - Restricted
        {
          path: "panorama",
          name: "panorama",
          component: () => import("../layouts/EmptyLayout.vue"),
          meta: { title: "全景达古" },
          redirect: { name: "panorama-immersive" },
          children: [
            {
              path: "",
              name: "panorama-immersive",
              component: () => import("../pages/ImmersiveCloudTourPage.vue"),
              meta: { title: "全景达古" },
            },
            {
              path: "live",
              name: "panorama-live",
              component: () => import("../pages/PanoramaLivePage.vue"),
              meta: { title: "景区慢直播" },
            },
            {
              path: "videos",
              name: "panorama-videos",
              component: () => import("../pages/PanoramaVideosPage.vue"),
              meta: { title: "景区视频" },
            },
            {
              path: "shorts",
              name: "panorama-shorts",
              component: () => import("../pages/PanoramaShortsPage.vue"),
              meta: { title: "短视频合集" },
            },
            {
              path: "gallery",
              name: "panorama-gallery",
              component: () => import("../layouts/EmptyLayout.vue"),
              meta: { title: "景区图集" },
              children: [
                {
                  path: "",
                  name: "panorama-gallery-index",
                  component: () => import("../pages/PanoramaGalleryPage.vue"),
                  meta: { title: "景区图集" },
                },
                {
                  path: ":season",
                  name: "panorama-gallery-season",
                  component: () =>
                    import("../pages/PanoramaGallerySeasonPage.vue"),
                  meta: { title: "" }, // 标题由 beforeEnter 动态设置
                  beforeEnter: (to, _from, next) => {
                    const season = to.params.season as string;
                    if (season) {
                      to.meta.title = getSeasonTitle(season);
                    }
                    next();
                  },
                },
              ],
            },
            {
              path: "dagu-er",
              name: "panorama-dagu-er",
              component: () => import("../pages/PanoramaDaguErPage.vue"),
              meta: { title: "达古er拍达古" },
            },
          ],
        },

        // 游客服务 - Restricted
        {
          path: "service",
          name: "service",
          component: () => import("../layouts/EmptyLayout.vue"),
          meta: { title: "游客服务" },
          children: [
            {
              path: "",
              name: "service-home",
              component: () => import("../pages/service/ServiceHomePage.vue"),
              meta: { title: "游客服务" },
            },
            {
              path: "food",
              name: "service-food",
              component: () => import("../pages/service/FoodRecommendPage.vue"),
              meta: { title: "美食推荐" },
            },
            {
              path: "food/:id",
              name: "service-food-detail",
              component: () => import("../pages/service/BusinessDetailPage.vue"),
              meta: { title: "美食推荐详情" },
            },
            {
              path: "traffic",
              name: "service-traffic",
              component: () =>
                import("../pages/service/ServiceTrafficPage.vue"),
              meta: { title: "交通指南" },
            },
            {
              path: "route",
              name: "service-route",
              component: () => import("../pages/service/ServiceRoutePage.vue"),
              meta: { title: "精品线路" },
            },
            {
              path: "stay",
              name: "service-stay",
              component: () => import("../pages/service/ServiceStayPage.vue"),
              meta: { title: "温馨住宿" },
            },
            {
              path: "stay/:id",
              name: "service-stay-detail",
              component: () => import("../pages/service/BusinessDetailPage.vue"),
              meta: { title: "温馨住宿详情" },
            },
            {
              path: "specialty",
              name: "service-specialty",
              component: () =>
                import("../pages/service/ServiceSpecialtyPage.vue"),
              meta: { title: "精致特产" },
            },
            {
              path: "play",
              name: "service-play",
              component: () => import("../pages/service/ServicePlayPage.vue"),
              meta: { title: "游玩攻略" },
            },
            {
              path: "entertainment",
              name: "service-entertainment",
              component: () =>
                import("../pages/service/ServiceEntertainmentPage.vue"),
              meta: { title: "休闲娱乐" },
            },
            {
              path: "entertainment/:id",
              name: "service-entertainment-detail",
              component: () => import("../pages/service/BusinessDetailPage.vue"),
              meta: { title: "休闲娱乐详情" },
            },
            {
              path: "map",
              name: "service-map",
              component: () => import("../pages/service/ServiceMapPage.vue"),
              meta: { title: "景区导览" },
            },
            {
              path: "tips",
              name: "service-tips",
              component: () => import("../pages/service/ServiceTipsPage.vue"),
              meta: { title: "游客须知" },
            },
            {
              path: "products",
              name: "service-products",
              component: () =>
                import("../pages/service/ServiceProductsPage.vue"),
              meta: { title: "旅游产品" },
            },
            {
              path: "products/sunshine-snow",
              name: "service-product-sunshine-snow",
              component: () =>
                import("../pages/service/SunshineSnowTourPage.vue"),
              meta: { title: "阳光冰雪游" },
            },
            {
              path: "products/ice-summer",
              name: "service-product-ice-summer",
              component: () => import("../pages/service/IceSummerTourPage.vue"),
              meta: { title: "冰雪消暑游" },
            },
            {
              path: "products/snow-mountain-forest",
              name: "service-product-snow-mountain-forest",
              component: () =>
                import("../pages/service/SnowMountainColorForestTourPage.vue"),
              meta: { title: "雪山彩林游" },
            },
            {
              path: "products/glacier-science",
              name: "service-product-glacier-science",
              component: () =>
                import("../pages/service/GlacierScienceTourPage.vue"),
              meta: { title: "冰川科普游" },
            },
            {
              path: "products/red-culture",
              name: "service-product-red-culture",
              component: () =>
                import("../pages/service/RedCultureTourPage.vue"),
              meta: { title: "红色文化游" },
            },
            {
              path: "questionnaire",
              name: "service-questionnaire",
              component: () =>
                import("../pages/service/ServiceQuestionnairePage.vue"),
              meta: { title: "问卷调查" },
            },
            {
              path: "complaints",
              name: "service-complaints",
              component: () =>
                import("../pages/service/ServiceComplaintsPage.vue"),
              meta: { title: "游客投诉" },
            },
          ],
        },

        {
          path: "gov",
          component: () => import("../layouts/EmptyLayout.vue"),
          children: [
            {
              path: "",
              name: "gov",
              component: () => import("../pages/GovPortalPage.vue"),
              meta: { title: "政务中心" },
            },
            {
              path: ":tab",
              name: "gov-tab",
              component: () => import("../pages/GovPage.vue"),
              meta: { title: "政务中心" },
            },
          ],
        },

        // 景区营销 - Restricted
        {
          path: "marketing",
          name: "marketing",
          component: () => import("../layouts/EmptyLayout.vue"),
          meta: { title: "景区营销" },
          children: [
            {
              path: "",
              name: "marketing-home",
              component: () => import("../pages/MarketingHomePage.vue"),
              meta: { title: "景区营销" },
            },
            {
              path: "campaigns",
              name: "marketing-campaigns",
              component: () => import("../pages/CampaignsPage.vue"),
              meta: { title: "全民营销" },
            },
            {
              path: "brand",
              name: "marketing-brand",
              component: () => import("../pages/CampaignsPage.vue"),
              meta: { title: "品牌活动" },
            },
            {
              path: "promotion",
              name: "marketing-promotion",
              component: () => import("../pages/CampaignsPage.vue"),
              meta: { title: "市场推介" },
            },
            {
              path: "cross",
              name: "marketing-cross",
              component: () => import("../pages/CampaignsPage.vue"),
              meta: { title: "跨界联动" },
            },
            {
              path: "mediaCare",
              name: "marketing-media",
              component: () => import("../pages/CampaignsPage.vue"),
              meta: { title: "媒体关注" },
            },
            {
              path: "party",
              name: "marketing-party",
              component: () => import("../pages/PartyPage.vue"),
              meta: { title: "党政活动" },
            },
            {
              path: "units",
              name: "marketing-units",
              component: () => import("../pages/UnitsPage.vue"),
              meta: { title: "职工活动" },
            },
          ],
        },

        // 研学达古 - Allowed Primary
        {
          path: "study",
          name: "study",
          component: () => import("../pages/StudyPage.vue"),
          meta: { title: "研学达古" },
        },
        {
          path: "p",
          name: "placeholder",
          component: () => import("../pages/PlaceholderPage.vue"),
        },
        {
          path: "news",
          name: "news-list",
          component: () => import("../pages/NewsListPage.vue"),
        },
        {
          path: "news/:id",
          name: "news-detail",
          component: () => import("../pages/NewsDetailPage.vue"),
        },
        {
          path: "404",
          name: "not-found",
          component: () => import("../pages/NotFoundPage.vue"),
        },
      ],
    },
    {
      path: "/:pathMatch(.*)*",
      redirect: "/404",
    },
  ],
});
